import { memo } from "react";

// Componente memoizado para el skeleton de tarjeta individual
const CardSkeleton = memo(function CardSkeleton({
  size = "normal",
}: {
  size?: "featured" | "normal";
}) {
  const isFeatured = size === "featured";
  const imageSize = isFeatured ? "w-40 h-40 md:w-48 md:h-48" : "w-36 h-36";

  return (
    <div
      className={`${
        isFeatured
          ? "flex flex-col items-center text-center gap-4 md:flex-row md:items-center md:gap-8 md:text-left"
          : "flex flex-col items-center text-center gap-3"
      }`}
    >
      {/* Avatar circular */}
      <div className={`relative ${imageSize}`}>
        <div className="w-full h-full rounded-full bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 relative overflow-hidden ring-4 ring-white shadow-xl">
          <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
        </div>
      </div>
      {/* Texto */}
      <div className={isFeatured ? "flex-1 space-y-3" : "space-y-2 w-full"}>
        <div
          className={`h-${
            isFeatured ? "7" : "5"
          } bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg ${
            isFeatured ? "w-52 md:w-64" : "w-32 mx-auto"
          } relative overflow-hidden`}
        >
          <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
        </div>
        <div
          className={`h-4 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg ${
            isFeatured ? "w-40 md:w-48" : "w-24 mx-auto"
          } relative overflow-hidden`}
        >
          <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
        </div>
      </div>
    </div>
  );
});

// Componente para el skeleton del header de sección
const SectionHeaderSkeleton = memo(function SectionHeaderSkeleton({
  variant = "default",
}: {
  variant?: "primary" | "default";
}) {
  const isPrimary = variant === "primary";

  return (
    <div
      className={`relative overflow-hidden rounded-xl ${
        isPrimary
          ? "bg-linear-to-r from-gray-300 via-gray-200 to-gray-300 py-5"
          : "bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 py-4"
      }`}
    >
      <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
      <div
        className={`${isPrimary ? "h-8" : "h-6"} bg-transparent w-48 mx-auto`}
      ></div>
    </div>
  );
});

const OrganizacionSkeleton = memo(function OrganizacionSkeleton() {
  return (
    <section className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 mb-16 animate-pulse">
      {/* Subtítulo descriptivo skeleton */}
      <div className="flex justify-center mb-10">
        <div className="h-6 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg w-72 relative overflow-hidden">
          <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
        </div>
      </div>

      {/* Gerencia General Skeleton */}
      <div className="mb-16">
        <SectionHeaderSkeleton variant="primary" />
        <div className="flex justify-center items-center mt-12">
          <div className="relative">
            <div className="absolute -inset-4 bg-linear-to-r from-gray-200/50 via-gray-300/50 to-gray-200/50 rounded-3xl blur-xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-100">
              <CardSkeleton size="featured" />
            </div>
          </div>
        </div>
      </div>

      {/* Vicepresidencias Skeleton */}
      <div className="mb-16">
        <SectionHeaderSkeleton />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-10">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
            >
              <CardSkeleton />
            </div>
          ))}
        </div>
      </div>

      {/* Gerencias Skeleton */}
      <div className="mb-8">
        <SectionHeaderSkeleton />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-10">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
            >
              <CardSkeleton />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

export default OrganizacionSkeleton;
